import BackButton from '@/components/back-button/back-button';
import Layout from '@/components/layout-questionnaire/layout-questionnaire';
import NavigationBar from '@/components/layout/navigation-bar/navigation-bar';
import MarkdownText from '@/components/markdown/markdown-text';
import { ROUTE_PPRA_QUESTIONNAIRE } from '@/constants/routes';
import useStickyButton from '@/hooks/use-sticky-button';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { HStack, ListItem, ListItemProps, UnorderedList, VStack } from '@chakra-ui/react';
import { Box, Button, ButtonProps, Flex, Image, Text, TextProps } from '@cmpsr/components';
import { Link, useNavigate } from 'react-router-dom';
import { DoubleWave } from '../ppra-loading-page/components';
import { ContentContainerV2 } from '../ppra-results-page/components/ContentContainer';
import StickyButton from '../ppra-results-page/components/sticky-button/StickyButton';

const h1: TextProps = {
  fontSize: { base: '22px', md: '35px' },
  fontWeight: '400',
  lineHeight: '1.3',
  textAlign: 'center',
};

const h2: TextProps = {
  fontSize: { base: '14px', md: '16px' },
  fontWeight: '400',
  lineHeight: '140%',
  letterSpacing: '1.4px',
  textTransform: 'uppercase',
};

const textStyle: TextProps = {
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '140%',
};

const industryProofs = {
  width: { base: '150px', md: '212px' },
  height: { base: '63px', md: '89px' },
  p: '15px 21px',
  bg: 'white',
  borderRadius: '10px',
  boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.25)',
  alignItems: 'center',
  justifyContent: 'center',
};

const ctaButtonStyle: ButtonProps = {
  backgroundColor: '#AFDFFF',
  color: '#183344',
  borderRadius: '100px',
  padding: '20px 30px',
  height: '55px',
  fontWeight: '500',
  fontSize: '16px',
  width: { base: 'auto', md: '308px' },
  _hover: { backgroundColor: '#9BCFEF' },
};

const listItemStyle: ListItemProps = {
  pl: '4px',
  ml: '4px',
};

export const PPRAMembershipInterstitialPage = () => {
  const navigate = useNavigate();
  const { showStickyButton, mainButtonRef } = useStickyButton();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const ctaLink = ROUTE_PPRA_QUESTIONNAIRE;
  const ctaText = 'Take Your Free Risk Assessment';

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <Layout background="background-page">
      <Box
        flexDirection="column"
        height="100%"
        width="100%"
        background="tan"
        alignItems="center"
        pb={{ base: '200px', md: '300px', lg: '500px' }}
        zIndex="0"
      >
        <NavigationBar variant="white" leftContent={<BackButton variant="dark" onClick={() => navigate(-1)} />} />
        <Flex flexDirection="column" height="100%" width="100%" justifyContent="center" alignItems="center">
          <ContentContainerV2 zIndex="1" gap={{ base: '18px', md: '25px' }}>
            {/* Welcome to Catch Section */}
            <VStack
              spacing={{ base: '10px', md: '18px' }}
              width="100%"
              align="center"
              pt={{ base: '20px', md: '75px' }}
            >
              <Text {...h2}>Welcome to Catch</Text>
              <Text {...h1}>
                The World's First{' '}
                <Text as="span" {...h1} fontWeight="700">
                  Cancer <br /> Prevention
                </Text>{' '}
                Platform
              </Text>
            </VStack>

            {/* Body Section */}
            <VStack spacing="26px" width={{ base: '100%', md: '480px' }}>
              {/* Form Section */}
              <VStack spacing="15px" width="100%" align="stretch">
                <Text {...textStyle}>For $5 a month, Catch members access:</Text>

                {/* Benefits Section */}
                <UnorderedList spacing="10px">
                  <ListItem {...listItemStyle}>
                    <MarkdownText {...textStyle}>
                      **Personalized Risk Assessment** covering 500+ risk factors across 21 cancers.
                    </MarkdownText>
                  </ListItem>
                  <ListItem {...listItemStyle}>
                    <MarkdownText {...textStyle}>
                      **Comprehensive Action Plan** with targeted interventions to reduce your risk.
                    </MarkdownText>
                  </ListItem>
                  <ListItem {...listItemStyle}>
                    <MarkdownText {...textStyle}>
                      **Annual Screening Protocol** for early cancer detection based on your unique risk profile.
                    </MarkdownText>
                  </ListItem>
                  <ListItem {...listItemStyle}>
                    <MarkdownText {...textStyle}>
                      **Member-Only Updates** summarizing the latest cutting-edge research.
                    </MarkdownText>
                  </ListItem>
                </UnorderedList>
              </VStack>

              {/* CTA Button */}
              <Flex justifyContent="center" width="100%">
                <Link to={ctaLink} style={{ textDecoration: 'none' }}>
                  <Button as="span" {...ctaButtonStyle} ref={mainButtonRef}>
                    {ctaText}
                  </Button>
                </Link>
              </Flex>
            </VStack>
          </ContentContainerV2>

          {/* Demo Section */}
          <Flex
            display={{ base: 'none', md: 'flex' }}
            gap="0px"
            justifyContent="center"
            width="100"
            my="50px"
            overflow="hidden"
          >
            <Image
              display={{ base: 'none', xl: 'block' }}
              width="316px"
              height="auto"
              src="/images/demos/app-demo-1.png"
              mt="100px"
            />
            <Image width="594px" height="453px" src="/images/dashboard.png" />
            <Flex display={{ base: 'none', xl: 'flex' }} flexDirection="column" gap="14px" mt="60px">
              <Image
                width="316px"
                height="120px"
                src="/images/demos/app-demo-2.png"
                boxShadow="0px 4px 14px rgba(0, 0, 0, 0.25)"
              />
              <Image
                width="316px"
                height="103px"
                src="/images/demos/app-demo-3.png"
                boxShadow="0px 4px 14px rgba(0, 0, 0, 0.25)"
              />
            </Flex>
          </Flex>

          {/* Research Note and Institution Logos */}
          <VStack spacing="26px" width="100%" align="center" pt={{ base: '36px', md: '20px' }} bg="tan">
            <Text
              width={{ base: '295px', md: '600px', lg: '850px' }}
              fontSize={{ base: '22px', md: '35px' }}
              fontWeight="400"
              color="#183344"
              textAlign="center"
              lineHeight="1.3"
              px="10px"
            >
              Based on Research from Leading Institutions & Data from 10,000+ Clinical Studies
            </Text>

            {/* Industry Proofs */}
            <Box width={{ base: '100%', lg: '100%' }} pt="10px" bg="tan">
              <HStack spacing="14px" justifyContent="center" display={{ base: 'none', lg: 'flex' }}>
                <Flex {...industryProofs}>
                  <Image src="/images/industry-proofs/harvard.svg" />
                </Flex>
                <Flex {...industryProofs}>
                  <Image src="/images/industry-proofs/american-cancer-society.svg" />
                </Flex>
                <Flex {...industryProofs}>
                  <Image src="/images/industry-proofs/stanford.svg" />
                </Flex>
                <Flex {...industryProofs}>
                  <Image src="/images/industry-proofs/american-college-of-radiology.svg" />
                </Flex>
                <Flex {...industryProofs}>
                  <Image src="/images/industry-proofs/johns-university.svg" />
                </Flex>
              </HStack>

              <VStack spacing="14px" display={{ base: 'flex', lg: 'none' }}>
                <HStack spacing="14px" justifyContent="center">
                  <Flex {...industryProofs}>
                    <Image src="/images/industry-proofs/harvard.svg" />
                  </Flex>
                  <Flex {...industryProofs}>
                    <Image src="/images/industry-proofs/american-cancer-society.svg" />
                  </Flex>
                </HStack>
                <HStack spacing="14px" justifyContent="center">
                  <Flex {...industryProofs}>
                    <Image src="/images/industry-proofs/stanford.svg" />
                  </Flex>
                  <Flex {...industryProofs}>
                    <Image src="/images/industry-proofs/johns-university.svg" />
                  </Flex>
                </HStack>
              </VStack>
            </Box>
          </VStack>
          {/* End: Industry Proofs */}

          {showStickyButton && <StickyButton ctaLink={ctaLink} ctaButtonStyle={ctaButtonStyle} ctaText={ctaText} />}

          {/* Footer */}
          <Box position="absolute" bottom="0" width="100%" background="transparent" zIndex="-10">
            <DoubleWave />
          </Box>
        </Flex>
      </Box>
    </Layout>
  );
};
